import { decodeFilterValues, encodeFilterValues, ICP_RATINGS, type IcpItem, type IcpJevItem, type IcpMark, type IcpRanked, type IcpRating } from '@nest-recruit/shared';

// ADR 0068: one filter per profile line Jev reads. A line's filter holds a rating (1–5); the rating says how strongly Jev must have found
// the line in someone's CV. It narrows the list and moves no order, no score and no tab. Pure, so tested.

/** The lines the filter bar offers a button for: skills and work habits, the lines Jev reads. English and facts have their own checks. */
export function jevLinesOf(items: readonly IcpItem[]): readonly IcpJevItem[] {
  return items.filter((item): item is IcpJevItem => item.kind === 'jev');
}

/** What each rating asks for, as the words on the pop-up's rows. Higher is stricter. */
export const LINE_RATING_WORDS: Readonly<Record<IcpRating, string>> = {
  5: 'Strong proof only',
  4: 'Shown in real work or better',
  3: 'Related or better',
  2: 'Only named or better',
  1: 'Any grade',
};

/** Jev's grades from strongest to weakest, as the profile ranking records them per line. */
const GRADES: readonly IcpMark[] = ['strong', 'shown', 'related', 'named', 'none'];

/** The grades a rating lets through: 5 only "strong", down to 1 every grade (the person only has to have been read). */
export function gradesForRating(rating: IcpRating): readonly IcpMark[] {
  return GRADES.slice(0, GRADES.length + 1 - rating);
}

/** A line's filter by the line's id: the rating it holds. A line with no entry is not filtered. */
export type LineFilters = Readonly<Record<string, IcpRating>>;

export function hasLineFilters(filters: LineFilters): boolean {
  return Object.keys(filters).length > 0;
}

const RATING_SEPARATOR = ':';

function ratingOf(text: string | undefined): IcpRating | null {
  const rating = ICP_RATINGS.find((candidate) => String(candidate) === text);
  return rating ?? null;
}

/** The address's `rline`: "lineId:rating" pairs. Only lines the filter bar offers are kept, so a link to a line since removed narrows nothing. */
export function lineFiltersOfSearch(value: string | undefined, items: readonly IcpItem[]): LineFilters {
  const offered = new Set(jevLinesOf(items).map((item) => item.id));
  const filters: Record<string, IcpRating> = {};
  for (const pair of decodeFilterValues(value)) {
    const [id, text] = pair.split(RATING_SEPARATOR);
    const rating = ratingOf(text);
    if (id !== undefined && rating !== null && offered.has(id)) filters[id] = rating;
  }
  return filters;
}

/** The address's part of a change: `undefined` clears the key. Lines go in the profile's order, so the same filters make the same address. */
export function lineFilterValueOf(filters: LineFilters, items: readonly IcpItem[]): string | undefined {
  return encodeFilterValues(jevLinesOf(items).flatMap((item) => (filters[item.id] === undefined ? [] : [`${item.id}${RATING_SEPARATOR}${filters[item.id]}`])));
}

/**
 * Whether a person stays with these filters on: Jev has read them, and for every filtered line their grade is one the rating lets
 * through. Someone not read (waiting, a scan, Jev not connected) has no grade to compare, so they are out while a filter is on.
 */
export function matchesLineFilters(ranked: IcpRanked | undefined, items: readonly IcpItem[], filters: LineFilters): boolean {
  const entries = Object.entries(filters);
  if (entries.length === 0) return true;
  if (!ranked || ranked.state !== 'read') return false;
  return entries.every(([id, rating]) => {
    const value = ranked.values[items.findIndex((item) => item.id === id)];
    return value !== undefined && !value.isExcluded && gradesForRating(rating).includes(value.mark);
  });
}

/** The most lower ratings the empty state offers for one line. */
const MAX_SUGGESTIONS = 3;

/** A lower rating that would show people, and how many. The count is what the list's "Showing n" would say after pressing it. */
export interface LineSuggestion {
  readonly rating: IcpRating;
  readonly count: number;
}

export interface LineFilterBlock {
  readonly lineId: string;
  readonly name: string;
  /** The rating the line is at now. */
  readonly rating: IcpRating;
  /** Lower ratings that would show at least one person, strictest first. Never empty. */
  readonly suggestions: readonly LineSuggestion[];
}

export interface LineFilterLine {
  readonly lineId: string;
  readonly name: string;
  readonly rating: IcpRating;
}

/**
 * What the empty list says when line filters leave nobody:
 * - `relax`: relaxing one line alone would show people; a block for each such line.
 * - `unread`: nobody in the list has been read by Jev, so no rating could show anyone.
 * - `nothing-found`: one line, people were read, and nobody shows it even at rating 1.
 * - `together`: several lines, and relaxing any single one still leaves nobody.
 */
export type LineFilterHelp =
  | { readonly kind: 'relax'; readonly blocks: readonly LineFilterBlock[] }
  | { readonly kind: 'unread' | 'nothing-found' | 'together'; readonly lines: readonly LineFilterLine[] };

/**
 * Why the list is empty and what would bring people back, for the line filters only. `rows` are the people the OTHER filters (keywords,
 * the normal filters) already let through, across every tab, so the counts here are exactly what the list's "Showing n" says after
 * pressing a suggestion. Null when nothing is wrong: no line filter is on, nobody is left to filter, or the filters still show someone.
 */
export function lineFilterHelpOf(rows: readonly { readonly applicationId: string }[], ranks: ReadonlyMap<string, IcpRanked> | undefined, items: readonly IcpItem[], filters: LineFilters): LineFilterHelp | null {
  const lines = jevLinesOf(items).filter((line) => filters[line.id] !== undefined);
  if (lines.length === 0 || rows.length === 0) return null;
  const countWith = (next: LineFilters): number => rows.filter((row) => matchesLineFilters(ranks?.get(row.applicationId), items, next)).length;
  if (countWith(filters) > 0) return null;

  const current = (id: string): IcpRating => filters[id] ?? 1;
  const blocks = lines.flatMap((line): LineFilterBlock[] => {
    const lower = [...ICP_RATINGS].reverse().filter((rating) => rating < current(line.id));
    // Strictest first. A rating that shows no more people than the stricter one before it adds nothing, so it is left out.
    const suggestions = lower
      .map((rating) => ({ rating, count: countWith({ ...filters, [line.id]: rating }) }))
      .filter((suggestion) => suggestion.count > 0)
      .filter((suggestion, index, all) => index === 0 || suggestion.count > (all[index - 1]?.count ?? 0));
    return suggestions.length === 0 ? [] : [{ lineId: line.id, name: line.text, rating: current(line.id), suggestions: suggestions.slice(0, MAX_SUGGESTIONS) }];
  });
  if (blocks.length > 0) return { kind: 'relax', blocks };

  const named = lines.map((line) => ({ lineId: line.id, name: line.text, rating: current(line.id) }));
  const isAnyoneRead = rows.some((row) => ranks?.get(row.applicationId)?.state === 'read');
  if (!isAnyoneRead) return { kind: 'unread', lines: named };
  return { kind: lines.length === 1 ? 'nothing-found' : 'together', lines: named };
}
